import { createContext } from 'react';

import type { TGridOrderQuadrantAbbreviations } from '@/modules/patientList/patientStrip/patientStripOdontogram/types';

export type ToothState =
  | 'NORMAL'
  | 'MISSING'
  | 'IMPLANT'
  | 'PONTIC'
  | 'IMPACTED'
  | 'UNKNOWN';
export type Plaque = 'NONE' | 'SLIGHT' | 'MODERATE' | 'HEAVY' | 'UNKNOWN';
export type Mobility =
  | 'NONE'
  | 'CLASS_I'
  | 'CLASS_II'
  | 'CLASS_III'
  | 'UNKNOWN';
export type BoneLoss = 'NONE' | 'SLIGHT' | 'MODERATE' | 'SEVERE' | 'UNKNOWN';

export interface ToothSurfaceMeasurements {
  probingDepth: number | null;
  gingivalMargin: number | null;
  gingivalMarginMm?: number | null;
  clinicalAttachmentLevel: number | null;
  hasBleeding: boolean | null;
  hasSuppuration: boolean | null;
  furcationGrade: number | null;
  mucogingivalJunction: number | null;
}

export interface ToothSurfaces {
  distal: ToothSurfaceMeasurements;
  center: ToothSurfaceMeasurements;
  mesial: ToothSurfaceMeasurements;
}

export interface PerioChartTooth {
  toothNumber: string | null;
  toothState?: ToothState | null;
  plaque?: Plaque | null;
  mobility?: Mobility | null;
  boneLoss?: BoneLoss | null;
  facialMeasurements?: ToothSurfaces | null;
  lingualMeasurements?: ToothSurfaces | null;
}

export interface PerioChartIncompleteReasons {
  bleedingNotRecorded: boolean;
  gingivalMarginNotRecorded: boolean;
  clinicalAttachmentLevelAlwaysMatchesProbingDepth: boolean;
}

export interface PerioChartResponse {
  examDate: string | null;
  teeth: PerioChartTooth[];
  isIncomplete: boolean;
  incompleteReasons?: PerioChartIncompleteReasons;
  insufficientBleedingForGeneralSrp: TGridOrderQuadrantAbbreviations[];
  insufficientBleedingForLocalSrp: TGridOrderQuadrantAbbreviations[];
}

export type Arch = 'upper' | 'lower';
export type ToothSide = 'facial' | 'lingual';
export type ToothSurface = keyof ToothSurfaces;
export type ToothSurfaceMeasurement = keyof ToothSurfaceMeasurements;

/**
 * The gingivalMarginCoronalDisplayMode preference allows users to customize how
 * gingival margin coronal tissue appears in the UI.
 *
 * Different PMSs represent gingival overgrowth differently. In Dentrix Core,
 * users enter gingival overgrowth with a plus sign +X, and the number appears
 * in the UI as X with a highlight over top of it. In Dentrix Ascend, gingival
 * overgrowth appears in the UI with a minus sign -X (like in Videa).
 */
export const GINGIVAL_MARGIN_CORONAL_DISPLAY_MODES = [
  'coronal_minus_sign',
  'coronal_plus_sign',
] as const;
export type GingivalMarginCoronalDisplayMode =
  (typeof GINGIVAL_MARGIN_CORONAL_DISPLAY_MODES)[number];

export const PerioChartModeContext = createContext<'light' | 'dark'>('dark');
